'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { getNewVideos, type MeiliListing } from '@/app/lib/meilisearch'

/** Truncate text to a maximum character length at the nearest word boundary */
function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  const truncated = text.slice(0, maxLength)
  const lastSpace = truncated.lastIndexOf(' ')
  return (lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated) + '…'
}

/** Inline play‑triangle SVG icon */
function PlayIcon() {
  return (
    <svg
      width="16"
      height="16"
      fill="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  )
}

/** Inline heart SVG icon */
function HeartIcon() {
  return (
    <svg
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
    </svg>
  )
}

/** Skeleton placeholder shown while the hero data is loading */
function HeroSkeleton() {
  return (
    <div
      className="relative flex items-end overflow-hidden"
      style={{ height: '100vh', minHeight: 600 }}
      aria-busy="true"
      aria-label="Loading hero section"
    >
      {/* Animated gradient shimmer */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(90deg, #0a0a0a 25%, #151515 50%, #0a0a0a 75%)',
          backgroundSize: '200% 100%',
          animation: 'hero-shimmer 1.8s ease-in-out infinite',
        }}
      />

      {/* Faint content placeholders */}
      <div className="relative z-10 px-6 pb-[90px] md:px-10 md:pb-[100px] lg:px-[72px] lg:pb-[120px]">
        <div className="mb-4 h-6 w-32 rounded bg-white/5" />
        <div className="mb-2 h-16 w-80 max-w-full rounded bg-white/5" />
        <div className="mb-6 h-5 w-48 rounded bg-white/5" />
        <div className="mb-8 h-12 w-72 max-w-full rounded bg-white/5" />
        <div className="flex gap-4">
          <div className="h-12 w-40 rounded bg-white/5" />
          <div className="h-12 w-36 rounded bg-white/5" />
        </div>
      </div>

    </div>
  )
}

/**
 * Full‑screen hero section that showcases the latest video from MeiliSearch.
 *
 * Features:
 * - Ken Burns zoom‑out animation on the background thumbnail
 * - Layered gradient overlays and gold grid texture
 * - Staggered fade‑up entrance animations for each content block
 * - Responsive layout adapting from mobile → tablet → desktop
 */
export default function HeroSection() {
  const [imageLoaded, setImageLoaded] = useState(false)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['hero-latest-video'],
    queryFn: () => getNewVideos({ limit: 1, offset: 0 }),
    staleTime: 5 * 60 * 1000,
  })

  const video: MeiliListing | undefined = data?.hits?.[0]

  /* Pre‑load the thumbnail so we can trigger the Ken Burns transition. */
  useEffect(() => {
    if (!video?.thumbnail_url) return

    const img = new window.Image()
    img.onload = () => setImageLoaded(true)
    img.src = video.thumbnail_url

    return () => {
      img.onload = null
    }
  }, [video?.thumbnail_url])

  /* ------------------------------------------------------------------ */
  /*  Loading state                                                      */
  /* ------------------------------------------------------------------ */
  if (isLoading || isError) {
    return <HeroSkeleton />
  }

  /* ------------------------------------------------------------------ */
  /*  Derived values                                                     */
  /* ------------------------------------------------------------------ */
  const description = video?.videodescription
    ? truncateText(video.videodescription, 150)
    : 'Discover premium content curated just for you.'

  const videoLink = video ? `/video/${video.listing_id}` : '#'

  /* ------------------------------------------------------------------ */
  /*  Render                                                             */
  /* ------------------------------------------------------------------ */
  return (
    <section
      className="relative flex items-end overflow-hidden"
      style={{ height: '100vh', minHeight: 600 }}
      aria-label="Featured video"
    >
      {/* ── Background image with Ken Burns effect ─────────────── */}
      {video?.thumbnail_url ? (
        <div
          className="absolute inset-0 transition-transform duration-[1.8s] ease-out"
          style={{
            backgroundImage: `url(${video.thumbnail_url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            transform: imageLoaded ? 'scale(1)' : 'scale(1.04)',
          }}
          role="img"
          aria-label={video.videotitle ?? 'Featured video thumbnail'}
        />
      ) : (
        /* Fallback gradient when no thumbnail is available */
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, #0a0a0a 0%, #1a1a0a 50%, #0a0a0a 100%)',
          }}
        />
      )}

      {/* ── Overlay gradients ──────────────────────────────────── */}
      <div
        className="absolute inset-0"
        style={{
          background: [
            'linear-gradient(to right, rgba(5,5,5,0.96) 0%, rgba(5,5,5,0.7) 45%, rgba(5,5,5,0.1) 100%)',
            'linear-gradient(to top, rgba(5,5,5,1) 0%, rgba(5,5,5,0.6) 30%, rgba(5,5,5,0) 60%)',
          ].join(', '),
        }}
        aria-hidden="true"
      />

      {/* ── Gold grid texture ──────────────────────────────────── */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: [
            'linear-gradient(rgba(212,160,23,0.03) 1px, transparent 1px)',
            'linear-gradient(90deg, rgba(212,160,23,0.03) 1px, transparent 1px)',
          ].join(', '),
          backgroundSize: '60px 60px',
          maskImage:
            'radial-gradient(ellipse 60% 80% at 30% 50%, black 0%, transparent 80%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 60% 80% at 30% 50%, black 0%, transparent 80%)',
        }}
        aria-hidden="true"
      />

      {/* ── Content ────────────────────────────────────────────── */}
      <div
        className="
          relative z-[2] w-full
          max-w-full px-6 pb-[90px]
          md:px-10 md:pb-[100px]
          lg:max-w-[680px] lg:px-[72px] lg:pb-[120px]
        "
      >
        {/* Badge */}
        <div
          className="
            clip-skew-sm animate-fade-up-delay-1
            mb-4 inline-flex items-center gap-2
            border font-condensed text-[11px] font-bold
            uppercase tracking-[3px]
          "
          style={{
            background: 'rgba(212,160,23,0.12)',
            borderColor: 'rgba(212,160,23,0.5)',
            color: '#F5C842',
            padding: '6px 16px',
          }}
        >
          <PlayIcon />
          <span>Deep Pockets</span>
        </div>

        {/* Title */}
        <h1
          className="
            animate-fade-up-delay-2
            mb-4 font-heading uppercase leading-[0.92] tracking-[2px]
          "
          style={{
            fontSize: 'clamp(44px, 7vw, 100px)',
          }}
        >
          {video?.videotitle ?? 'Latest Release'}
        </h1>

        {/* Meta pills */}
        <div
          className="animate-fade-up-delay-3 mb-8 flex flex-wrap gap-4"
          style={{ animationDelay: '0.35s' }}
        >
          {video?.videocategory && (
            <span
              className="
                font-condensed text-xs font-bold uppercase tracking-[1.5px]
                text-muted-foreground
              "
              style={{
                background: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.12)',
                padding: '4px 12px',
              }}
            >
              {video.videocategory}
            </span>
          )}
        </div>

        {/* Description */}
        <p
          className="
            animate-fade-up-delay-3
            mb-8 max-w-[480px] text-[15px] leading-[1.7] text-muted-foreground
          "
        >
          {description}
        </p>

        {/* Action buttons */}
        <div className="animate-fade-up-delay-4 flex flex-wrap gap-4">
          {/* Play Now */}
          <Link
            href={videoLink}
            className="
              clip-skew group
              inline-flex items-center gap-[10px]
              font-condensed text-[15px] font-black uppercase tracking-[2.5px]
              transition-all duration-200
              hover:-translate-y-0.5
            "
            style={{
              background: 'linear-gradient(135deg, #D4A017, #E8820C)',
              color: '#000000',
              padding: '14px 32px',
              boxShadow:
                '0 0 40px rgba(212,160,23,0.35), 0 8px 24px rgba(0,0,0,0.5)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow =
                '0 0 60px rgba(212,160,23,0.5), 0 12px 32px rgba(0,0,0,0.6)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow =
                '0 0 40px rgba(212,160,23,0.35), 0 8px 24px rgba(0,0,0,0.5)'
            }}
          >
            <PlayIcon />
            <span>Play Now</span>
          </Link>

          {/* Favorites */}
          <button
            type="button"
            className="
              clip-skew
              inline-flex items-center gap-[10px]
              font-condensed text-[15px] font-bold uppercase tracking-[2px]
              text-white transition-all duration-200
            "
            style={{
              background: 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(255,255,255,0.2)',
              padding: '14px 28px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.12)'
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.35)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.07)'
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'
            }}
            onClick={() => toast('Coming soon', { icon: '⭐' })}
            aria-label="Add to favorites"
          >
            <HeartIcon />
            <span>Favorites</span>
          </button>
        </div>
      </div>
    </section>
  )
}
